import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Divider
} from '@fluentui/react-components';
import { ArrowSync24Regular } from '@fluentui/react-icons';
import { dashboardService } from '../../services/dashboardService';
import { snapshotService } from '../../services/snapshotService';
import { dlqService } from '../../services/dlqService';
import { SummaryCards, OperationMetricsCards, QueueChartsSection } from '../../components/Dashboard';
import type { DashboardResponse } from '../../types';

// Tipo para pontos do gráfico de mensagens ativas
interface ActiveChartDataPoint {
  time: string;
  count: number;
  isHourlyAggregate?: boolean;
}

// Tipo para pontos do gráfico de DLQ e Scheduled
interface QueueCountsChartDataPoint {
  time: string;
  count: number;
  isHourlyAggregate?: boolean;
}

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sectionTimestamp: {
    fontSize: '11px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground4,
  },
  divider: {
    marginBottom: '24px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
  },
  errorContainer: {
    marginBottom: '24px',
  },
});

export const HomePage: React.FC = () => {
  const styles = useStyles();
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Estado para mensagens ativas (gráfico em tempo real)
  const [activeHistory, setActiveHistory] = useState<ActiveChartDataPoint[]>([]);
  const [currentActiveCount, setCurrentActiveCount] = useState<number>(0);
  const [incomingPerSecond, setIncomingPerSecond] = useState<number | null>(null);
  const [incomingPerMinute, setIncomingPerMinute] = useState<number | null>(null);
  const [outgoingPerSecond, setOutgoingPerSecond] = useState<number | null>(null);
  const [outgoingPerMinute, setOutgoingPerMinute] = useState<number | null>(null);
  const activePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentHourRef = useRef<number | null>(null);
  const lastMetricsRef = useRef<{ count: number; time: number } | null>(null);
  const incomingRateHistoryRef = useRef<number[]>([]); // Histórico de taxas de entrada para média
  const outgoingRateHistoryRef = useRef<number[]>([]); // Histórico de taxas de saída para média

  // Estado para DLQ e Scheduled em tempo real (atualiza a cada 10s)
  const [realtimeDlqCount, setRealtimeDlqCount] = useState<number | null>(null);
  const [realtimeScheduledCount, setRealtimeScheduledCount] = useState<number | null>(null);
  const queueCountsPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Estado para histórico de DLQ e Scheduled (gráficos em tempo real)
  const [dlqHistory, setDlqHistory] = useState<QueueCountsChartDataPoint[]>([]);
  const [scheduledHistory, setScheduledHistory] = useState<QueueCountsChartDataPoint[]>([]);
  const queueCountsHourRef = useRef<number | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      setError(null);
      const data = await dashboardService.getDashboard();
      setDashboard(data);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Erro ao carregar o dashboard. Verifique se a API está disponível.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Carrega historico das ultimas 24h (agregado por hora + hora atual) e depois inicia polling em tempo real
  useEffect(() => {
    // Funcao para carregar historico agregado (horas passadas + hora atual)
    const loadAggregatedHistory = async () => {
      try {
        const response = await snapshotService.getAggregatedActiveMessagesHistory();

        // Usa a hora local do navegador para comparacao de mudanca de hora
        currentHourRef.current = new Date().getHours();

        // Pontos agregados por hora (historico) - labels ja vem formatados pelo backend no fuso de SP
        const hourlyPoints: ActiveChartDataPoint[] = response.hourlyData.map(point => ({
          time: point.hourLabel,  // "14:00", "15:00", etc (ja no fuso de SP)
          count: point.maxCount,
          isHourlyAggregate: true
        }));

        // Pontos da hora atual (granularidade de 1 minuto) - converte para horario local
        const currentHourPoints: ActiveChartDataPoint[] = response.currentHourData.map(point => ({
          time: new Date(point.time).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          count: point.count,
          isHourlyAggregate: false
        }));

        setActiveHistory([...hourlyPoints, ...currentHourPoints]);

        // Define contagem atual como o ultimo ponto
        const allPoints = [...hourlyPoints, ...currentHourPoints];
        if (allPoints.length > 0) {
          setCurrentActiveCount(allPoints[allPoints.length - 1].count);
        }
      } catch (err) {
        console.error('Erro ao carregar historico agregado de mensagens ativas:', err);
      }
    };

    // Funcao para buscar metricas (polling) - usa novo endpoint com contador de entrantes
    const fetchActiveMetrics = async () => {
      try {
        const result = await snapshotService.getActiveMetrics();
        const now = new Date();
        const nowTime = now.getTime();
        const currentHour = now.getHours();

        // Verifica se cruzamos para uma nova hora - recarrega dados agregados
        if (currentHourRef.current !== null && currentHour !== currentHourRef.current) {
          await loadAggregatedHistory();
          return;
        }

        // Calcula taxas de entrada e saida baseado nas metricas
        if (lastMetricsRef.current) {
          const timeDiffSeconds = (nowTime - lastMetricsRef.current.time) / 1000;
          if (timeDiffSeconds > 0) {
            // Taxa de entrada (do contador do backend)
            const incomingRate = result.incomingCount / timeDiffSeconds;
            setIncomingPerSecond(Math.round(incomingRate * 10) / 10); // 1 casa decimal

            // Taxa de saida = entrada - delta no count
            const countDelta = result.activeCount - lastMetricsRef.current.count; // Positivo = fila cresceu
            const outgoingRate = Math.max(0, incomingRate - (countDelta / timeDiffSeconds));
            setOutgoingPerSecond(Math.round(outgoingRate * 10) / 10);

            // Guarda historico das taxas para calcular media por minuto (ultimos 60 segundos)
            incomingRateHistoryRef.current.push(incomingRate);
            outgoingRateHistoryRef.current.push(outgoingRate);
            if (incomingRateHistoryRef.current.length > 60) {
              incomingRateHistoryRef.current.shift();
            }
            if (outgoingRateHistoryRef.current.length > 60) {
              outgoingRateHistoryRef.current.shift();
            }

            // Calcula media das taxas = msgs/min
            const avgIncoming = incomingRateHistoryRef.current.reduce((a, b) => a + b, 0) / incomingRateHistoryRef.current.length;
            const avgOutgoing = outgoingRateHistoryRef.current.reduce((a, b) => a + b, 0) / outgoingRateHistoryRef.current.length;
            setIncomingPerMinute(Math.round(avgIncoming * 60));
            setOutgoingPerMinute(Math.round(avgOutgoing * 60));
          }
        }
        lastMetricsRef.current = { count: result.activeCount, time: nowTime };

        setCurrentActiveCount(result.activeCount);
        setActiveHistory(prev => {
          // Apenas dados agregados por hora + 1 unico ponto com hora atual
          const hourlyPoints = prev.filter(p => p.isHourlyAggregate);

          // Ponto atual com hora:minuto:segundo
          const timeLabel = now.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });

          const nowPoint: ActiveChartDataPoint = {
            time: timeLabel,
            count: result.activeCount,
            isHourlyAggregate: false
          };

          return [...hourlyPoints, nowPoint];
        });
      } catch (err) {
        console.error('Erro ao buscar metricas de mensagens ativas:', err);
      }
    };

    // 1. Carrega historico agregado primeiro
    loadAggregatedHistory();

    // 2. Configura polling a cada 1 segundo para tempo real
    activePollingRef.current = setInterval(fetchActiveMetrics, 1000);

    return () => {
      if (activePollingRef.current) {
        clearInterval(activePollingRef.current);
      }
    };
  }, []);

  // Carrega histórico de DLQ e Scheduled e configura polling
  useEffect(() => {
    // Função para carregar histórico agregado
    const loadQueueCountsHistory = async () => {
      try {
        const response = await dlqService.getAggregatedHistory();
        queueCountsHourRef.current = new Date().getHours();

        // Pontos agregados por hora (histórico) - DLQ
        const dlqHourlyPoints: QueueCountsChartDataPoint[] = response.hourlyData.map(point => ({
          time: point.hourLabel,
          count: point.maxDlqCount,
          isHourlyAggregate: true
        }));

        // Pontos agregados por hora (histórico) - Scheduled
        const scheduledHourlyPoints: QueueCountsChartDataPoint[] = response.hourlyData.map(point => ({
          time: point.hourLabel,
          count: point.maxScheduledCount,
          isHourlyAggregate: true
        }));

        // Ponto atual (apenas o mais recente da hora atual)
        if (response.currentHourData.length > 0) {
          const lastPoint = response.currentHourData[response.currentHourData.length - 1];
          const timeLabel = new Date(lastPoint.time).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });

          const dlqNowPoint: QueueCountsChartDataPoint = {
            time: timeLabel,
            count: lastPoint.dlqCount,
            isHourlyAggregate: false
          };

          const scheduledNowPoint: QueueCountsChartDataPoint = {
            time: timeLabel,
            count: lastPoint.scheduledCount,
            isHourlyAggregate: false
          };

          setDlqHistory([...dlqHourlyPoints, dlqNowPoint]);
          setScheduledHistory([...scheduledHourlyPoints, scheduledNowPoint]);
        } else {
          setDlqHistory(dlqHourlyPoints);
          setScheduledHistory(scheduledHourlyPoints);
        }

        // Atualiza contagens em tempo real
        if (response.currentHourData.length > 0) {
          const lastPoint = response.currentHourData[response.currentHourData.length - 1];
          setRealtimeDlqCount(lastPoint.dlqCount);
          setRealtimeScheduledCount(lastPoint.scheduledCount);
        }
      } catch (err) {
        console.error('Erro ao carregar histórico de DLQ/Scheduled:', err);
      }
    };

    // Função para buscar contagens atuais (polling)
    const fetchQueueCounts = async () => {
      try {
        const now = new Date();
        const currentHour = now.getHours();

        // Verifica se cruzamos para uma nova hora - recarrega dados agregados
        if (queueCountsHourRef.current !== null && currentHour !== queueCountsHourRef.current) {
          await loadQueueCountsHistory();
          return;
        }

        const summary = await dlqService.getSummary();
        setRealtimeDlqCount(summary.deadLetterMessageCount);
        setRealtimeScheduledCount(summary.scheduledMessageCount);

        // Atualiza o ponto atual nos gráficos
        const timeLabel = now.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });

        // Atualiza gráfico de DLQ
        setDlqHistory(prev => {
          const hourlyPoints = prev.filter(p => p.isHourlyAggregate);
          const nowPoint: QueueCountsChartDataPoint = {
            time: timeLabel,
            count: summary.deadLetterMessageCount,
            isHourlyAggregate: false
          };
          return [...hourlyPoints, nowPoint];
        });

        // Atualiza gráfico de Scheduled
        setScheduledHistory(prev => {
          const hourlyPoints = prev.filter(p => p.isHourlyAggregate);
          const nowPoint: QueueCountsChartDataPoint = {
            time: timeLabel,
            count: summary.scheduledMessageCount,
            isHourlyAggregate: false
          };
          return [...hourlyPoints, nowPoint];
        });
      } catch (err) {
        console.error('Erro ao buscar contagens de DLQ/Scheduled:', err);
      }
    };

    // 1. Carrega histórico agregado primeiro
    loadQueueCountsHistory();

    // 2. Configura polling a cada 10 segundos
    queueCountsPollingRef.current = setInterval(fetchQueueCounts, 10000);

    return () => {
      if (queueCountsPollingRef.current) {
        clearInterval(queueCountsPollingRef.current);
      }
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
  };

  if (loading && !dashboard) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title2 className={styles.title}>Dashboard</Title2>
        </div>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <span>Carregando dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title2 className={styles.title}>Dashboard</Title2>
        <Button
          appearance="primary"
          icon={refreshing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorContainer}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Parte 1: Summary Cards - Totais */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Totais no Microsoft 365 Education
          {dashboard?.summary?.lastUpdated && (
            <span className={styles.sectionTimestamp}>
              - Atualizado: {dashboardService.formatDate(dashboard.summary.lastUpdated)}
            </span>
          )}
        </div>
        <SummaryCards summary={dashboard?.summary ?? null} loading={loading} />
      </div>

      <Divider className={styles.divider} />

      {/* Parte 2: Operation Metrics Cards */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Operações nas Últimas 24 Horas
          {dashboard?.operations?.lastUpdated && (
            <span className={styles.sectionTimestamp}>
              - Atualizado: {dashboardService.formatDate(dashboard.operations.lastUpdated)}
            </span>
          )}
        </div>
        <OperationMetricsCards operations={dashboard?.operations ?? null} loading={loading} />
      </div>

      <Divider className={styles.divider} />

      {/* Parte 3: Queue Charts */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          Filas nas Últimas 24 Horas
        </div>
        <QueueChartsSection
          queueCharts={dashboard?.queueCharts ?? null}
          loading={loading}
          activeHistory={activeHistory}
          currentActiveCount={currentActiveCount}
          incomingPerSecond={incomingPerSecond}
          incomingPerMinute={incomingPerMinute}
          outgoingPerSecond={outgoingPerSecond}
          outgoingPerMinute={outgoingPerMinute}
          realtimeDlqCount={realtimeDlqCount}
          realtimeScheduledCount={realtimeScheduledCount}
          dlqHistory={dlqHistory}
          scheduledHistory={scheduledHistory}
        />
      </div>
    </div>
  );
};

export default HomePage;
